import { useRef, useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridNoRecords,
  GridColumn as Column
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ColumnMenu } from '../components/columnMenu';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import Toolbar from '../components/page-toolbar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { NoRecords } from '../components/grid/no-records';
import { appStore } from '../stores/app-store';
import { GetAllUsersForNetworkReportAsync } from '../services/users';
import {
  CardViewDataStateChangeEvent,
  NetworkMembersCard
} from './network-members-card';
import HelpSupport from '../components/help-support';
import { useGetAllUsers } from '../hooks/services/use-users';
import { UserResponse } from '../types/responses/user-response';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'username', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const NetworkMembersGrid = () => {
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');
  // const [users, setUsers] = useState<DataResult>({
  //   data: [],
  //   total: 0
  // });

  const { data, isLoading, mutate } = useGetAllUsers(
    toDataSourceRequestString(dataState),
    'network'
  );

  // useEffect(() => {
  //   async function fetchUsers() {
  //     try {
  //       const response = await GetAllUsersForNetworkAsync(dataState);
  //       setUsers(response);
  //     } catch (ex) {
  //       console.error(ex);
  //     }
  //   }
  //   fetchUsers();
  // }, [dataState]);

  const reportExportRef = useRef<ExcelExport | null>(null);

  const onExportExcel = async () => {
    appStore.showLoading();
    try {
      await GetAllUsersForNetworkReportAsync().then((response) => {
        if (reportExportRef.current !== null) {
          reportExportRef.current.save(response);
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      appStore.hideLoading();
    }
  };

  const EmailCell = (props: GridCellProps) => {
    const handleCopy = () => {
      navigator.clipboard.writeText(props.dataItem.username);
    };

    return (
      <td className="k-table-td" style={{ overflow: 'hidden' }}>
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ gap: 4, minWidth: 0 }}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1
            }}
            title={props.dataItem.username}>
            {props.dataItem.username}
          </span>

          <span
            className="material-symbols-outlined email-cell-copy text-primary"
            onClick={handleCopy}>
            content_copy
          </span>
        </div>
      </td>
    );
  };

  const RoleCell = (props: GridCellProps) => {
    const splitter = props.dataItem.specialRoles.map((role: string) => (
      <li>{role}</li>
    ));

    return (
      <td className={'k-table-td'}>
        <ul
          style={{
            margin: 0,
            padding: '0 0 0 16px'
          }}>
          {splitter}
        </ul>
      </td>
    );
  };

  return (
    <>
      <Toolbar title={'All users'}>
        <HelpSupport
          title="My network Members guide"
          url="https://forum.catalystglobal.com/t/4630"
        />
      </Toolbar>
      <ExcelExport ref={reportExportRef} fileName={'UsersReport.xlsx'}>
        <ExcelExportColumn field="fullName" title="Name" width={300} />
        <ExcelExportColumn field="username" title="Email address" width={350} />
        <ExcelExportColumn field="phoneNumber" title="Phone" width={200} />
        <ExcelExportColumn field="specialRole" title="Role" width={200} />
        <ExcelExportColumn field="department" title="Department" width={200} />
        <ExcelExportColumn
          field="organisation"
          title="Organisation"
          width={250}
        />
        <ExcelExportColumn field="country" title="Country" width={200} />
      </ExcelExport>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search users'}
          columnsToSearch={['username', 'fullName']}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          exportRef={reportExportRef}
          useCustomExportExcel={true}
          handleCustomExportToExcel={onExportExcel}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>

        {gridMode === 'grid' && (
          <Grid
            id={'grid-user'}
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            data={data}
            {...dataState}
            onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column field={'fullName'} title={'Name'} columnMenu={ColumnMenu} />
            <Column
              field={'username'}
              filter={'text'}
              title={'Email address'}
              columnMenu={ColumnMenu}
              cells={{
                data: (props) => EmailCell(props)
              }}
            />
            <Column
              field={'phoneNumber'}
              title={'Phone'}
              columnMenu={ColumnMenu}
              width={180}
            />
            <Column
              field={'specialRole'}
              title={'Role'}
              columnMenu={ColumnMenu}
              cells={{
                data: (props) => RoleCell(props)
              }}
              width={150}
            />
            <Column
              field={'department'}
              title={'Department'}
              columnMenu={ColumnMenu}
              width={150}
            />
            <Column
              field={'organisation'}
              title={'Organisation'}
              columnMenu={ColumnMenu}
              width={150}
            />
            <Column
              field={'country'}
              title={'Country'}
              columnMenu={ColumnMenu}
              width={120}
            />

            <GridNoRecords>
              {isLoading ? (
                <div
                  className={
                    'd-flex flex-column align-items-center gap-2 text-muted'
                  }>
                  <Loader type={'pulsing'} />
                  <span>Loading data</span>
                </div>
              ) : (
                <NoRecords />
              )}
            </GridNoRecords>
          </Grid>
        )}
        {gridMode === 'card' && (
          <NetworkMembersCard
            data={(data?.data ?? []) as UserResponse[]}
            total={data?.total}
            {...dataState}
            onDataStateChange={(e: CardViewDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
          />
        )}
      </div>
    </>
  );
};
export default NetworkMembersGrid;
